import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  MatCheckbox,
  MatCheckboxDefaultOptions,
  MAT_CHECKBOX_DEFAULT_OPTIONS,
} from '@angular/material/checkbox';
import { match } from 'assert/strict';

import { PscWithVendorsDataModel } from '../../../../models/PscWithVendorsDataModel';
import { UnspscCodeWithVendorsDataModel } from '../../../../models/UnspscCodeWithVendorsDataModel';
import { VendorWithProductCountModel } from '../../../../models/VndorWithProductCountModel';
import { PSCData } from '../../../../services/cccReport/ccc-report.service';
import { GoogleAnalyticsService } from '../../../../services/google-analytics/google-analytics.service';
import {
  ParametersService,
  UnspscCode,
} from '../../../../services/Prameters/parameters.service';
import { UserService } from '../../../../services/user/user.service';
import {
  IChipEntry,
  IDropDownListEntry,
} from '../../mdm-input/mdm-input-settings';
import { ICccReportParameters } from './cccReportParameters';

@Component({
  selector: 'app-ccc-report-parameters-component',
  templateUrl: './ccc-report-parameters.component.html',
  styleUrls: ['./ccc-report-parameters.component.scss'],
  standalone: false,
  providers: [
    {
      provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
      useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions,
    },
  ],
})
export class CccReportParametersComponent {
  selectedReportType = 'unspsc';
  selectedSortType = 'products';

  loadingUnspscCodes: boolean = false;
  unspscCodes: IDropDownListEntry[] = [];
  selectedUnspscCodes: IChipEntry[] = [];

  waitingForUnspscOrPscSelection: boolean = true;
  vendorsLoading: boolean = false;

  loadingPscDescriptions: boolean = false;
  pscDescriptions: IDropDownListEntry[] = [];
  selectedPscCategories: IChipEntry[] = [];

  loadingSyncGroupDescriptions: boolean = false;
  syncGroupDescriptions: IDropDownListEntry[] = [];
  selectedSyncGroupDescriptions: IChipEntry[] = [];

  loadingSyncSubcategories: boolean = false;
  syncSubCategories: IDropDownListEntry[] = [];
  selectedSyncSubCategories: IChipEntry[] = [];

  associatedVendors: VendorWithProductCountModel[] = [];
  inputVendors: IDropDownListEntry[] = [];
  selectedInputVendors: IChipEntry[] = [];
  outputVendors: IDropDownListEntry[] = [];
  selectedOutputVendors: IChipEntry[] = [];

  allPscs: PSCData[] = [];

  derivedCheckbox: boolean = true;
  attributionCheckbox: boolean = true;

  internalCCCReportTypes = [
    { name: 'UNSPSC', id: 'unspsc', selected: true },
    { name: 'VCC', id: 'psc', selected: false },
    { name: 'VSC', id: 'sync', selected: false },
  ];
  externalCCCReportTypes = [
    { name: 'UNSPSC', id: 'unspsc', selected: true },
    { name: 'VCC', id: 'psc', selected: false },
  ];
  sortTypes = [
    { name: 'List Vendors by # of products', id: 'products', selected: true },
    { name: 'List Vendors alphabetically', id: 'alpha', selected: false },
  ];

  @Input() searchInProgress: boolean = false;
  @Input() internalUser: boolean = false;

  @Output() searchClicked: EventEmitter<ICccReportParameters> =
    new EventEmitter<ICccReportParameters>();
  @Output() cancelSearchClicked: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private parameters: ParametersService,
    public gaService: GoogleAnalyticsService,
  ) {
    this.loadingPscDescriptions = true;
    this.loadingUnspscCodes = true;
    this.loadingSyncGroupDescriptions = true;
    this.loadingSyncSubcategories = true;
    this.parameters.GetAllPscs().then(r => {
      let ordered = r.sort((p1, p2) => {
        const a = p1.description.toLowerCase();
        const b = p2.description.toLowerCase();
        return a < b ? -1 : a > b ? 1 : 0;
      });
      this.allPscs = [];
      ordered.forEach(o => {
        this.pscDescriptions.push({
          isDisabled: false,
          isSelected: false,
          key: o.description,
          value: o.description,
        });
      });
      this.loadingPscDescriptions = false;
    });

    this.parameters.GetAllUnspscCodes().then(r => {
      let ordered = r.sort((p1, p2) => {
        return +p1.code - +p2.code;
      });
      this.unspscCodes = [];
      ordered.forEach(p =>
        this.unspscCodes.push({
          isDisabled: false,
          isSelected: false,
          key: p.code,
          value: this.internalUser
            ? p.code + ' - ' + p.description
            : p.description,
        }),
      );
      this.loadingUnspscCodes = false;
    });

    this.parameters.GetSyncCodes().then(s => {
      let ordered = s.sort((p1, p2) => {
        return p1.syncCode
          .toLowerCase()
          .localeCompare(p2.syncCode.toLowerCase());
      });
      this.syncGroupDescriptions = [];
      ordered.forEach(p =>
        this.syncGroupDescriptions.push({
          isDisabled: false,
          isSelected: false,
          key: p.subCategoryDescription,
          value:
            p.syncCode +
            ' - ' +
            p.groupDescription +
            ' - ' +
            p.subCategoryDescription,
        }),
      );
      this.loadingSyncGroupDescriptions = false;
      this.loadingSyncSubcategories = false;
    });
  }

  sortRadioChange(val: any) {
    this.selectedSortType = val.value;
    this.sortVendors();
  }

  radioChange(val: any) {
    this.selectedReportType = val.value;
    this.selectedPscCategories = [];
    this.selectedUnspscCodes = [];
    this.selectedSyncGroupDescriptions = [];
    this.selectedSyncSubCategories = [];
    this.unselectDropdownEntries(this.pscDescriptions);
    this.unselectDropdownEntries(this.unspscCodes);
    this.unselectDropdownEntries(this.syncGroupDescriptions);
    this.unselectDropdownEntries(this.syncSubCategories);
    this.dataUpdate();
  }

  unselectDropdownEntries(data: IDropDownListEntry[]) {
    data.forEach(d => (d.isSelected = false));
  }

  clearParameters() {
    this.selectedPscCategories = [];
    this.selectedUnspscCodes = [];
    this.selectedSyncGroupDescriptions = [];
    this.selectedSyncSubCategories = [];
    this.unselectDropdownEntries(this.pscDescriptions);
    this.unselectDropdownEntries(this.unspscCodes);
    this.unselectDropdownEntries(this.syncGroupDescriptions);
    this.unselectDropdownEntries(this.syncSubCategories);
    this.dataUpdate();
  }

  areVendorsMatched(): boolean {
    if (this.selectedInputVendors.length == 0) return false;
    if (this.selectedInputVendors.length != this.selectedOutputVendors.length)
      return false;
    let matched = true;
    this.selectedInputVendors.forEach(iv => {
      let found = this.selectedOutputVendors.find(({ key }) => key == iv.key);
      if (found == null) matched = false;
    });
    return matched;
  }

  matchVendorsClick() {
    let keys: string[] = [];
    this.selectedInputVendors.forEach(v => {
      if (keys.indexOf(v.key) == -1) keys.push(v.key);
    });
    this.selectedOutputVendors.forEach(v => {
      if (keys.indexOf(v.key) == -1) keys.push(v.key);
    });

    keys.forEach(k => {
      let iv = this.inputVendors.find(e => e.key == k);
      if (!iv.isSelected) {
        iv.isSelected = true;
        this.selectedInputVendors.push({ key: iv.key, value: iv.value });
      }
      let ov = this.outputVendors.find(e => e.key == k);
      if (!ov.isSelected) {
        ov.isSelected = true;
        this.selectedOutputVendors.push({ key: ov.key, value: ov.value });
      }
    });
  }

  cancelSearch() {
    this.cancelSearchClicked.emit();
  }

  pullReport() {
    let parameters: ICccReportParameters = {
      CccReportType: this.selectedReportType,
      InputVendors: [],
      OutputVendors: [],
      IncludeAttribution: this.attributionCheckbox,
      IncludeDerived: this.derivedCheckbox,
      SameVendorSet: false,
    };
    if (this.selectedReportType == 'unspsc') {
      parameters.ProductSpendCategory = '';
      parameters.SyncSubCategory = '';
      let u = this.selectedUnspscCodes[0];
      parameters.UnspscCode = u.key;
      parameters.UnspscDescription = this.internalUser
        ? u.key + ' - ' + u.value
        : u.value;
    }
    if (this.selectedReportType == 'psc') {
      parameters.UnspscCode = '';
      parameters.UnspscDescription = '';
      parameters.SyncSubCategory = '';
      parameters.ProductSpendCategory = this.selectedPscCategories[0].key;
    }
    if (this.selectedReportType == 'sync') {
      parameters.UnspscCode = '';
      parameters.UnspscDescription = '';
      parameters.ProductSpendCategory = '';
      parameters.SyncSubCategory = this.selectedSyncGroupDescriptions[0].key;
    }

    this.selectedInputVendors.forEach(v => {
      parameters.InputVendors.push(v.key);
    });
    this.selectedOutputVendors.forEach(v => {
      parameters.OutputVendors.push(v.key);
    });

    parameters.SameVendorSet = this.areVendorsMatched();

    this.gaService.cccPullCrossesEvent(parameters);
    this.searchClicked.emit(parameters);
  }

  pullReportAllowed(): boolean {
    if (
      this.selectedReportType == 'unspsc' &&
      this.selectedUnspscCodes.length == 0
    )
      return false;
    if (
      this.selectedReportType == 'psc' &&
      this.selectedPscCategories.length == 0
    )
      return false;
    if (
      this.selectedReportType == 'sync' &&
      this.selectedSyncGroupDescriptions.length == 0
    )
      return false;
    if (this.selectedInputVendors.length == 0) return false;
    if (this.selectedOutputVendors.length == 0) return false;
    return true;
  }

  disableVendorSelection(): boolean {
    if (
      this.selectedReportType == 'unspsc' &&
      this.selectedUnspscCodes.length == 0
    )
      return true;
    if (
      this.selectedReportType == 'psc' &&
      this.selectedPscCategories.length == 0
    )
      return true;
    if (
      this.selectedReportType == 'sync' &&
      this.selectedSyncGroupDescriptions.length == 0
    )
      return true;

    return this.vendorsLoading;
  }

  derivedCheckboxClick(): void {
    this.derivedCheckbox = !this.derivedCheckbox;
  }

  isDerivedChecked(): boolean {
    return this.derivedCheckbox;
  }

  attributionCheckboxClick(): void {
    this.attributionCheckbox = !this.attributionCheckbox;
  }

  isAttributeChecked(): boolean {
    return this.attributionCheckbox;
  }

  clearVendors() {
    this.inputVendors = [];
    this.outputVendors = [];
    this.selectedInputVendors = [];
    this.selectedOutputVendors = [];
    this.unselectDropdownEntries(this.inputVendors);
    this.unselectDropdownEntries(this.outputVendors);
  }

  sortVendors() {
    this.inputVendors = [];
    this.outputVendors = [];
    this.associatedVendors
      .sort((v1, v2) => {
        if (this.selectedSortType == 'products') {
          return v2.numberOfProducts - v1.numberOfProducts;
        } else {
          const a = v1.vendorName.toLowerCase();
          const b = v2.vendorName.toLowerCase();
          return a < b ? -1 : a > b ? 1 : 0;
        }
      })
      .forEach(v => {
        this.inputVendors.push({
          isDisabled: false,
          isSelected: false,
          key: v.vendorName,
          value: v.vendorName,
        });
        this.outputVendors.push({
          isDisabled: false,
          isSelected: false,
          key: v.vendorName,
          value: v.vendorName,
        });
      });
    this.selectedInputVendors.forEach(siv => {
      this.inputVendors.filter(iv => iv.key == siv.key)[0].isSelected = true;
    });
    this.selectedOutputVendors.forEach(sov => {
      this.outputVendors.filter(ov => ov.key == sov.key)[0].isSelected = true;
    });
  }

  setUpVendorLists() {
    this.clearVendors();
    this.sortVendors();
  }

  dataUpdate() {
    this.clearVendors();
    switch (this.selectedReportType) {
      case 'unspsc':
        if (this.selectedUnspscCodes.length == 0) {
          this.associatedVendors = [];
          this.waitingForUnspscOrPscSelection = true;
        } else {
          this.waitingForUnspscOrPscSelection = false;
          this.vendorsLoading = true;
          const unspsc = this.selectedUnspscCodes[0].key;
          this.parameters.GetVendorsByUnspscCode(unspsc).then(r => {
            this.vendorsLoading = false;
            this.associatedVendors = r;
            this.setUpVendorLists();
          });
        }
        break;
      case 'psc':
        if (this.selectedPscCategories.length == 0) {
          this.associatedVendors = [];
          this.waitingForUnspscOrPscSelection = true;
        } else {
          this.waitingForUnspscOrPscSelection = false;
          this.vendorsLoading = true;
          const psc = this.selectedPscCategories[0].key;
          this.parameters.GetVendorsByPSCDescription(psc).then(r => {
            this.vendorsLoading = false;
            this.associatedVendors = r;
            this.setUpVendorLists();
          });
        }
      case 'sync':
        if (this.selectedSyncGroupDescriptions.length == 0) {
          // || this.selectedSyncSubCategories.length == 0
          this.associatedVendors = [];
          this.waitingForUnspscOrPscSelection = true;
        } else {
          this.waitingForUnspscOrPscSelection = false;
          this.vendorsLoading = true;
          const sync = this.selectedSyncGroupDescriptions[0].key;
          this.parameters.GetVendorsBySyncSubcategory(sync).then(r => {
            this.vendorsLoading = false;
            this.associatedVendors = r;
            this.setUpVendorLists();
          });
        }
        break;
    }
  }
}
